import { injectable } from "inversify";
import "reflect-metadata";
import { Events } from "../shared/enum";

export interface MessageWithCallback {
    message: string;
    callback?: () => void;
}

export interface IMessageQueueService {
    enqueueMessages(
        messages: string[],
        eventEmitter: Phaser.Events.EventEmitter,
        scene: Phaser.Scene,
        delayMs?: number
    ): void;
    enqueueMessagesWithCallbacks(
        messagesWithCallbacks: MessageWithCallback[],
        eventEmitter: Phaser.Events.EventEmitter,
        scene: Phaser.Scene,
        delayMs?: number
    ): void;
    clear(): void;
    isPlaying(): boolean;
    onQueueComplete(callback: Function): void;
}

@injectable()
export class MessageQueueService implements IMessageQueueService {
    private messageQueue: MessageWithCallback[] = [];
    private isProcessing: boolean = false;
    private currentTimer: Phaser.Time.TimerEvent | null = null;
    private currentEventEmitter: Phaser.Events.EventEmitter | null = null;
    private currentScene: Phaser.Scene | null = null;
    private queueCompleteCallback: Function | null = null;

    public enqueueMessages(
        messages: string[],
        eventEmitter: Phaser.Events.EventEmitter,
        scene: Phaser.Scene,
        delayMs: number = 1500
    ): void {
        // Convert string messages to MessageWithCallback format (no callbacks)
        const messagesWithCallbacks = messages.map((msg) => ({ message: msg }));
        this.enqueueMessagesWithCallbacks(messagesWithCallbacks, eventEmitter, scene, delayMs);
    }

    public enqueueMessagesWithCallbacks(
        messagesWithCallbacks: MessageWithCallback[],
        eventEmitter: Phaser.Events.EventEmitter,
        scene: Phaser.Scene,
        delayMs: number = 750
    ): void {
        // Prevent enqueueing while already processing
        if (this.isProcessing) {
            console.warn("Message queue already processing. New messages ignored.");
            return;
        }

        // Store messages and context
        this.messageQueue = [...messagesWithCallbacks];
        this.currentEventEmitter = eventEmitter;
        this.currentScene = scene;
        this.isProcessing = true;

        console.log(`MessageQueueService: Enqueueing ${messagesWithCallbacks.length} messages`);

        // Start processing first message
        this.processNextMessage(delayMs);
    }

    private processNextMessage(delayMs: number): void {
        // FIX: Safety check - if scene or emitter cleared externally, stop processing
        if (!this.currentScene || !this.currentEventEmitter) {
            console.log("MessageQueueService: Scene/emitter cleared, stopping queue");
            this.messageQueue = [];
            this.isProcessing = false;
            this.queueCompleteCallback = null;
            return;
        }

        // Queue complete - cleanup and callback
        if (this.messageQueue.length === 0) {
            console.log("MessageQueueService: Queue complete");
            this.isProcessing = false;
            this.currentEventEmitter = null;
            this.currentScene = null;

            if (this.queueCompleteCallback) {
                this.queueCompleteCallback();
                this.queueCompleteCallback = null;
            }
            return;
        }

        // Get next message with optional callback
        const messageWithCallback = this.messageQueue.shift();
        if (this.currentEventEmitter && messageWithCallback) {
            // Execute callback BEFORE displaying message (to update state first)
            if (messageWithCallback.callback) {
                messageWithCallback.callback();
            }

            // Display the message
            console.log(
                `MessageQueueService: Displaying message - "${messageWithCallback.message}"`
            );
            this.currentEventEmitter.emit(Events.MESSAGE, messageWithCallback.message);
        }

        // Schedule next message after delay
        if (this.currentScene) {
            this.currentTimer = this.currentScene.time.addEvent({
                delay: delayMs,
                callback: () => this.processNextMessage(delayMs),
                callbackScope: this,
            });
        }
    }

    public clear(): void {
        console.log("MessageQueueService: Clearing queue");
        this.messageQueue = [];
        this.isProcessing = false;
        if (this.currentTimer) {
            this.currentTimer.remove();
            this.currentTimer = null;
        }
        this.currentEventEmitter = null;
        this.currentScene = null;
        this.queueCompleteCallback = null;
    }

    public isPlaying(): boolean {
        return this.isProcessing;
    }

    public onQueueComplete(callback: Function): void {
        this.queueCompleteCallback = callback;
    }
}
